import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import FlyupLabel from 'common/FlyupLabel';
import formatValue from 'utils/formatValue';
import { withStyles } from 'theme/withStyles';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import { withFieldProps } from 'hooks/useFieldProps';

import inputStyles from './styles/input';
import rootStyles from './styles/root';

const styles = ({ forms, palette, spacing, typography }) => ({
  input: {
    ...inputStyles({ forms, palette, typography }),
    paddingLeft: 12,
  },
  root: {
    ...rootStyles({ spacing }),
    '& .usd': {
      fontSize: 18,
      position: 'absolute',
      top: 10,
    },
  },
});

const Amount = ({
  classes,
  error,
  fieldDescrOverride,
  fieldDomId,
  format,
  help,
  loading,
  name,
  onBlur,
  onChange,
  onFocus,
  onPaste,
  placeholder,
  readOnly,
  required,
  shouldValidate,
  tooltip,
  validate,
  value,
  visuallyHideLabelText,
}) => (
  <>
    <div className={classnames('form-group', { error, loading }, classes.root)}>
      <Spinner loading={loading} />
      <FlyupLabel
        fieldName={name}
        error={error}
        value={value}
        tooltip={tooltip}
        htmlFor={fieldDomId}
        required={required}
        fieldDescrOverride={fieldDescrOverride}
        visuallyHideLabelText={visuallyHideLabelText}
      />
      <span className="usd">$</span>
      <input
        type="text"
        inputMode="decimal"
        className={classnames('form-control', classes.input)}
        name={name}
        id={fieldDomId}
        value={
          value && typeof value === 'number'
            ? value.toLocaleString('en-US', { minimumFractionDigits: 2 })
            : value || ''
        }
        placeholder={placeholder}
        step="0.01"
        readOnly={readOnly}
        required={required}
        aria-invalid={error}
        onChange={(e) => {
          const rawValue = e.target.value.replace(/[$,.]/g, '');
          /* only allow numbers and periods only allow 1 period */
          if (rawValue.match(/[^0-9]/g)) {
            return;
          }
          if (!e.target.value.length) {
            onChange(name, '');
          } else {
            onChange(name, formatValue(rawValue, format));
          }
          if (error) {
            validate(false);
          }
        }}
        onPaste={onPaste}
        onBlur={(e) => {
          const rawValue = e.target.value.replace(/[$,.]/g, '');
          const [first, ...rest] = rawValue.split('');
          if (first === '0' && rest.length === 1) {
            onChange(name, formatValue(rest.join(''), format));
          }
          onBlur();
          if (shouldValidate) {
            validate();
          }
        }}
        onFocus={onFocus}
        autoComplete="off"
        aria-describedby={
          classnames({
            [`help-${name}`]: help,
            [`error-${name}`]: error,
          }) || undefined
        }
      />
      <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
      <Help help={help} id={`help-${name}`} />
    </div>

    <ErrorMsg name={name} />
  </>
);

Amount.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDescrOverride: PropTypes.string,
  fieldDomId: PropTypes.string.isRequired,
  format: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onPaste: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  shouldValidate: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  visuallyHideLabelText: PropTypes.bool,
};

Amount.defaultProps = {
  fieldDescrOverride: undefined,
  // shouldValidate: true,
  visuallyHideLabelText: false,
};

const AmountStyled = withStyles(styles)(Amount);

export { AmountStyled as AmountBase };

export default withFieldProps()(AmountStyled);
